import { ArrowFillDownIcon, ArrowFillRightIcon, css, styled, TargetIcon } from 'canoe';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { cssSelectorForElement } from '@candu-ai/placement/utils';

import { LayerContext } from '../context/LayerContext';
import { isSelectableNode } from '../utils';

const WrappingChildrenLayer = styled.div`
  ${({ theme }) => css`
    border-left: 1px solid ${theme.colors.GlitterGrayL10};
    transition: ${theme.transitions.default()};

    margin-left: ${theme.spaces.xxs};
    padding-left: 6px;

    &.isHighlighted {
      border-left: 1px solid ${theme.colors.AquaTealL30};
    }
  `}
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    margin: ${theme.spaces.xs} 0;
  `}
`;

const WrapperLabel = styled.div`
  display: flex;
  flex-direction: inline;
  width: 100%;
  align-items: center;
  overflow: hidden;

  span:last-of-type {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const StyledWrapper = styled.div<{
  isSelected?: boolean;
  hasChildren?: boolean;
}>`
  ${({ theme, hasChildren }) => css`
    position: relative;
    outline: none;
    border: 0;

    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid transparent;
    background-color: ${theme.colors.GlitterGrayL10};
    color: ${theme.colors.OnyxGray};
    height: 28px;
    padding: ${theme.spaces.xxs} ${theme.spaces.xs};
    font-size: ${theme.fonts.small.fontSize};
    min-width: 0;
    border-radius: ${theme.borderRadius.small};
    transition: ${theme.transitions.default('background')};
    margin: 0;

    ${!hasChildren &&
    css`
      padding-left: 13px;
    `}

    cursor: pointer;

    button {
      transition: ${theme.transitions.default('opacity')};
      opacity: 0;
    }

    &.isDisabled {
      background: ${theme.colors.GlitterGrayL30};
      color: ${theme.colors.OnyxGrayL20};
      pointer-events: none;
    }

    &.isHovered {
      background: ${theme.colors.AquaTealL30};

      button {
        opacity: 1;
      }
    }

    &.isSelected {
      background: ${theme.colors.AquaTeal};
      color: ${theme.colors.White};
      font-weight: ${theme.fontWeights.bold};

      path {
        fill: ${theme.colors.White};
        stroke: ${theme.colors.White};
      }
    }

    :focus,
    :hover,
    :active {
      outline: none;
    }
  `}
`;

export interface DOMLayerProps {
  node: HTMLElement;
  document: Document;
}

const blackListCanvasNodes = [
  'script',
  'noscript',
  'candu-panel',
  'candu-chrome-hover',
  'candu-chrome-tutorial',
];

export const DOMLayer: React.FC<DOMLayerProps> = ({ node, document }: DOMLayerProps) => {
  const { selectedNodeId, hoveredNodeId, isOverDomLayer, handleFocus } = useContext(LayerContext);
  const initialRendering = useRef<boolean>(true);

  const isRootNode = node.tagName === 'body';
  const [expanded, setExpanded] = useState(isRootNode);

  const nodeId = useMemo(() => {
    return cssSelectorForElement(node, document) || node?.textContent;
  }, [node]);

  const isCanvasNode = !!node?.childElementCount;
  const Icon = expanded ? ArrowFillDownIcon : ArrowFillRightIcon;

  const isSelected = selectedNodeId === nodeId;
  const isHovered = hoveredNodeId === nodeId;

  useEffect(() => {
    // useEffect should fire only for external change of hover/selected node ... expects when it mounts for the first time (expand the selected node)
    if ((!isCanvasNode || isOverDomLayer) && !initialRendering.current) {
      return;
    }

    initialRendering.current = false;
    try {
      const hasSelectedNode =
        selectedNodeId === nodeId ||
        !!(selectedNodeId && !!node?.querySelectorAll(selectedNodeId).length);
      const hasHovereddNode =
        hoveredNodeId === nodeId ||
        !!(hoveredNodeId && !!node?.querySelectorAll(hoveredNodeId).length);

      setExpanded(hasSelectedNode || hasHovereddNode);
    } catch (e) {
      setExpanded(false);
    }
  }, [hoveredNodeId, selectedNodeId, nodeId, isOverDomLayer, isCanvasNode]);
  const isCanduNode = node?.getAttribute?.(`data-candu-slug`);
  const isSelectable = isSelectableNode(node);

  const isDescendantHighlighted = useMemo(() => {
    return Array.from(node.childNodes).some(
      (childNode: HTMLElement) => hoveredNodeId === cssSelectorForElement(childNode, document),
    );
  }, [node, hoveredNodeId]);

  const classNames = `
  ${!isSelectable ? 'isDisabled' : ''}
  ${isHovered ? 'isHovered' : ''}
  ${isSelected ? 'isSelected' : ''}`.trim();

  const wrappingChildrenClassNames = `
  ${isDescendantHighlighted ? 'isHighlighted' : ''}
  `.trim();

  const toggleExpand = useCallback(() => setExpanded((oldExpanded) => !oldExpanded), []);

  if (
    ![1, 3].includes(node.nodeType) ||
    !nodeId?.trim() ||
    blackListCanvasNodes.some((blNode) => new RegExp(blNode).test(nodeId ?? '')) ||
    nodeId === 'SCRIPT_PLACEHOLDER'
  ) {
    return null;
  }

  const nodeLabel = isCanduNode ? `Candu Content: ${isCanduNode}` : nodeId;
  const childNodes = Array.from(node.childNodes) || [];

  return (
    <>
      <Wrapper>
        <StyledWrapper
          data-cy={`domlayer-${nodeId}`}
          data-placement-id={nodeId}
          className={classNames}
          onFocus={handleFocus}
          tabIndex={0}
          hasChildren={childNodes.length > 0}
        >
          <WrapperLabel>
            {isSelectable && childNodes.length > 0 && (
              <Icon
                onClick={toggleExpand}
                color="OnyxGrayL20"
                size="s"
                mr="xxs"
                data-placement-toggle
                data-cy={`domlayer-${nodeId}-arrow`}
              />
            )}
            <span title={nodeLabel}>{nodeLabel}</span>
          </WrapperLabel>
          {isSelected && <TargetIcon data-cy="confirm-placement" data-confirm-placement />}
        </StyledWrapper>

        {expanded && node && !isCanduNode ? (
          <WrappingChildrenLayer className={wrappingChildrenClassNames}>
            {childNodes.map((n: HTMLElement, idx) => (
              <DOMLayer key={`${nodeId}-${idx.toString()}`} node={n} document={document} />
            ))}
          </WrappingChildrenLayer>
        ) : null}
      </Wrapper>
    </>
  );
};
